import { FileText, Sparkles } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import AWSAIPractitionerPage from '../../../pages/AWSAIPractitionerPage';
import AwsCloudPartitionerPage from '../../../pages/AwsCloudPartitionerPage';
import MicrosoftAIOverviewPage from '../../../pages/MicrosoftAIOverviewPage';
import MicrosoftOverviewPage from '../../../pages/MicrosoftOverviewPage';
import ResultsTablePage from '../../../pages/ResultsTablePage';
import { setCurrentTab } from '../../../redux/Slices/CloudPrepSlice';
import { setIsStartTest } from '../../../redux/Slices/QuestionsSlice';
import { useAppSelector } from '../../../redux/store/hooks';
import { cn } from '../../../utils/tailwindMerge';
import Button from '../../Common/Button';
import AITestList from '../aws/aiTests/AITestList';
import GCPOverview from '../gcp/GCPOverview';

const CloudPrepTabs = () => {
  const dispatch = useDispatch();
  const { currentTab } = useAppSelector(state => state.root.cloudPrep);
  const { openCard } = useAppSelector(state => state.root.cloudPrep);
  const navigate = useNavigate();
  const { id, subId } = useParams();
  const { userId } = useAppSelector(state => state.root.auth);

  const tabs = [
    {
      id: 'overView',
      label: 'Overview',
      icon: FileText,
      fullLabel: 'Overview'
    },
    {
      id: 'aiTests',
      label: 'Sample AI Tests',
      icon: Sparkles,
      fullLabel: 'Sample AI Tests'
    }
    //{
    //  id: 'results',
    //  label: 'Results',
    //  icon: Briefcase,
    //  fullLabel: 'Results'
    //}
  ];
  const currentTabIndex = tabs.findIndex(tab => tab.id === currentTab);
  const isFirst = currentTabIndex === 0;
  const isLast = currentTabIndex === tabs.length - 1;

  const renderOverView = () => {
    switch (subId) {
      case 'AWS_CLOUD_PRACTITIONER':
        return <AwsCloudPartitionerPage />;
      case 'AWS_AI_PRACTITIONER':
        return <AWSAIPractitionerPage />;
      case 'AZURE_DP_900':
        return <MicrosoftOverviewPage />;
      case 'AZURE_AI_900':
        return <MicrosoftAIOverviewPage />;
      case 'GOOGLE_CLOUD_DIGITAL_LEADER':
        return <GCPOverview />;
      case 'GOOGLE_AI_LEADER':
        return <GCPOverview />;
      default:
        return null;
    }
  };

  const renderResults = () => {
    return <ResultsTablePage isCloudPrepTest={true} userId={userId} />;
  };

  const renderAITest = () => {
    switch (subId) {
      case 'AWS_CLOUD_PRACTITIONER':
        return <AITestList />;
      case 'AWS_AI_PRACTITIONER':
        return <AITestList />;
      case 'AZURE_DP_900':
        return <AITestList />;
      case 'AZURE_AI_900':
        return <AITestList />;
      case 'GOOGLE_CLOUD_DIGITAL_LEADER':
        return <AITestList />;
      case 'GOOGLE_AI_LEADER':
        return <AITestList />;
      default:
        return renderResults();
    }
  };

  const renderTabContent = () => {
    switch (currentTab) {
      case 'overView':
        return renderOverView();
      case 'results':
        return renderResults();
      case 'aiTests':
        return renderAITest();
      default:
        return null;
    }
  };

  return (
    <div className='mx-auto w-full p-3 sm:max-w-6xl sm:p-5 lg:p-6'>
      <div className='flex justify-end py-4'>
        <Button
          label='Back to Cloud Prep'
          variant='contained'
          handleClickBtn={() => {
            dispatch(setIsStartTest(false));
            navigate('/cloud-prep');
          }}
        />
      </div>
      {/* Tabs Container */}
      <div className='w-full overflow-hidden rounded-b-2xl  rounded-tl-xl rounded-tr-xl bg-[#eef3ff]'>
        {/* Desktop Tabs */}
        <div className='flex hidden justify-between bg-[#eef3ff] sm:flex'>
          {tabs.map((tab, index) => {
            const Icon = tab.icon;
            const isActive = currentTab === tab.id;

            // Determine if it's the first or last tab for left/right rounding
            const isFirst = index === 0;
            const isLast = index === tabs.length - 1;

            return (
              <button
                key={tab.id}
                onClick={() => dispatch(setCurrentTab(tab.id as any))}
                className={`flex items-center gap-2 px-6 py-3 font-semibold transition-colors duration-200 ${
                  isActive
                    ? `rounded-tab ${isFirst ? 'left' : ''} ${
                        isLast ? 'right' : ''
                      } bg-[#3366FF] text-white`
                    : 'text-gray-700 hover:bg-gray-100 '
                }`}
              >
                <Icon size={18} />
                <span>{tab.fullLabel}</span>
              </button>
            );
          })}
        </div>

        {/* Mobile Tabs */}
        <div className='flex overflow-x-auto rounded-tl-md rounded-tr-md border-b border-gray-200 bg-[#eef3ff] sm:hidden'>
          {tabs.map((tab, index) => {
            const Icon = tab.icon;
            const isActive = currentTab === tab.id;
            const isFirst = index === 0;
            const isLast = index === tabs.length - 1;

            return (
              <button
                key={tab.id}
                onClick={() => dispatch(setCurrentTab(tab.id as any))}
                className={`flex min-w-[90px] flex-col items-center gap-1 px-4 py-3 font-semibold transition-colors duration-200 md:min-w-[120px] ${
                  isActive
                    ? `rounded-tab ${isFirst ? 'left' : ''} ${
                        isLast ? 'right' : ''
                      } bg-white text-gray-900`
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon size={18} />
                <span className='text-xs'>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div
          className={cn(
            isFirst ? 'rounded-tr-xl' : 'rounded-tl-xl',
            !isFirst && !isLast ? 'rounded-2xl ' : '',
            'rounded-b-2xl border-2 border-[#3366FF] bg-white'
          )}
        >
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default CloudPrepTabs;
